import React, { useState } from "react";
import { Search, Sun, Moon, Mail, Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import bmLogo from "../assets/BM-Logo.png";
import NewsletterModal from "./NewsletterModal";

export default function Header({
  query,
  setQuery,
  theme,
  toggleTheme,
  searchPlaceholder = "search...",
}) {
  const { pathname } = useLocation();
  const isFunding = pathname.startsWith("/funding") || pathname.startsWith("/o/");
  const [newsletterOpen, setNewsletterOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navCls = (active) =>
    `whitespace-nowrap rounded-md border px-3 py-1.5 text-[13px] font-medium transition-colors ${
      active
        ? "border-brand/50 bg-brand/10 text-foreground"
        : "border-border bg-secondary/40 text-muted-foreground hover:border-ring hover:text-foreground"
    }`;

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/60 bg-background/80 backdrop-blur-md">
      <div className="mx-auto max-w-[1000px] px-4 py-3">

        {/* ── Main row ── */}
        <div className="flex items-center gap-3">
          {/* Logo */}
          <Link to="/" className="flex shrink-0 items-center gap-2.5">
            <div className="rounded-lg border border-border/70 bg-[#f15b58] p-[3px]">
              <img src={bmLogo} alt="BuilderMantra logo" className="h-7 w-7 rounded-md object-cover" />
            </div>
            <span className="text-[17px] font-bold tracking-tight text-foreground">
              buildermantra<span className="text-brand">.com</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="ml-1 hidden items-center gap-1 md:flex">
            <Link to="/" className={navCls(!isFunding)}>Events</Link>
            <Link to="/funding" className={navCls(isFunding)}>Funding &amp; Programs</Link>
            <button
              data-testid="newsletter-nav-button"
              onClick={() => setNewsletterOpen(true)}
              className="flex shrink-0 whitespace-nowrap items-center gap-1.5 rounded-md border border-border bg-secondary/40 px-3 py-1.5 text-[13px] font-medium text-muted-foreground transition-colors hover:border-ring hover:text-foreground"
            >
              <Mail className="h-3.5 w-3.5" /> Daily Newsletter
            </button>
          </nav>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Desktop search */}
          <div className="relative hidden w-[280px] md:block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-9 w-full rounded-md border border-border bg-secondary/50 pl-9 pr-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-ring focus:bg-secondary"
            />
          </div>

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-secondary/50 text-foreground transition-colors hover:bg-secondary"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          {/* Mobile hamburger */}
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMobileMenuOpen((o) => !o)}
            aria-label="Toggle menu"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-secondary/50 text-foreground transition-colors hover:bg-secondary md:hidden"
          >
            {mobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>

        {/* ── Mobile search ── */}
        <div className="relative mt-2.5 md:hidden">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-9 w-full rounded-md border border-border bg-secondary/50 pl-9 pr-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-ring focus:bg-secondary"
          />
        </div>

        {/* ── Mobile nav dropdown ── */}
        {mobileMenuOpen && (
          <nav className="mt-2.5 flex flex-col gap-1.5 pb-1 md:hidden">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={navCls(!isFunding) + " w-full text-center"}
            >
              Events
            </Link>
            <Link
              to="/funding"
              onClick={() => setMobileMenuOpen(false)}
              className={navCls(isFunding) + " w-full text-center"}
            >
              Funding &amp; Programs
            </Link>
            <button
              data-testid="newsletter-nav-button-mobile"
              onClick={() => { setNewsletterOpen(true); setMobileMenuOpen(false); }}
              className="flex w-full items-center justify-center gap-1.5 rounded-md border border-border bg-secondary/40 px-3 py-2 text-[13px] font-medium text-muted-foreground transition-colors hover:border-ring hover:text-foreground"
            >
              <Mail className="h-3.5 w-3.5" /> Daily Newsletter
            </button>
          </nav>
        )}
      </div>

      <NewsletterModal open={newsletterOpen} onOpenChange={setNewsletterOpen} />
    </header>
  );
}
